import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, NgModule, Optional, Inject, InjectionToken } from '@angular/core';
import { createEmptyDashboard } from '@sick-curie/dashboard-builder-models';
import { dashboardElements } from '@sick-curie/dashboard-builder-ui-models';
import { mergeWith, isArray } from 'lodash-es';
import { Subject, of } from 'rxjs';
import { getElementPropertyType } from '@sick-curie/dashboard-builder-core';

/**
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
class DashboardStructureService {
    constructor() {
        this.structureChanged$ = new Subject();
        this.root = {
            type: 'root',
            element: undefined,
            children: [],
        };
    }
    static getContainedUiElementNode(node) {
        return node.children.find((child) => DashboardStructureService.isUiElement(child.element));
    }
    static isUiElement(element) {
        return dashboardElements.getDescription(element?.tag)?.role === 'ui';
    }
    static traverse(startNode, fun) {
        fun(startNode);
        startNode.children.forEach((c) => DashboardStructureService.traverse(c, fun));
    }
    findParent(node, startNode) {
        if (!startNode) {
            startNode = this.root;
        }
        const { children } = startNode;
        if (children.some((child) => child === node)) {
            return startNode;
        }
        for (const child of children) {
            const result = this.findParent(node, child);
            if (result) {
                return result;
            }
        }
        return undefined;
    }
    get structureChanged() {
        return this.structureChanged$;
    }
    normalizeRootNode(node) {
        node.children.forEach((gridItem) => {
            if (gridItem.child && !gridItem.children) {
                gridItem.children = [gridItem.child];
                gridItem.child = undefined;
            }
        });
    }
    addElementTo(parent, element) {
        if (parent.type === 'grid' && DashboardStructureService.isUiElement(element)) {
            throw new Error('Cannot add two main elements into single grid node');
        }
        const node = {
            type: 'element',
            element,
            children: [],
        };
        parent.children = parent.children.concat(node);
        this.structureChanged$.next();
        return node;
    }
    addToRoot(node) {
        this.root.children = this.root.children.concat(node);
        this.structureChanged$.next();
    }
    removeNode(node) {
        const parent = this.findParent(node);
        if (!parent) {
            return;
        }
        if (parent.type === 'grid' && DashboardStructureService.isUiElement(parent.element)) {
            throw new Error('Cannot delete the content of a grid node without deleting the grid node itself');
        }
        const index = parent.children.indexOf(node);
        if (index >= 0) {
            parent.children =
                parent.type === 'root'
                    ? parent.children.filter((_, i) => i !== index)
                    : parent.children.filter((_, i) => i !== index);
        }
        this.structureChanged$.next();
    }
    modifyElement(node, change) {
        node.element = mergeWith({}, node.element, change, (objValue, srcValue) => {
            if (isArray(objValue)) {
                return [...srcValue];
            }
            return undefined;
        });
        this.structureChanged$.next();
    }
    setRootNode(node) {
        DashboardStructureService.traverse(node, (n) => {
            if (n.element?.tag && !dashboardElements.getDescription(n.element?.tag)) {
                throw new Error('Unknown element ' + n.element.tag);
            }
        });
        this.root = node;
        this.structureChanged$.next();
    }
    getRootNode() {
        return this.root;
    }
    getElementsByTag(tag) {
        const foundElements = [];
        DashboardStructureService.traverse(this.root, (n) => {
            if (n.type === 'element' && n.element.tag === tag) {
                foundElements.push(n);
            }
        });
        return foundElements;
    }
}
DashboardStructureService.EMPTY = createEmptyDashboard();
DashboardStructureService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardStructureService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DashboardStructureService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardStructureService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardStructureService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class DashboardBuilderServicesModule {
}
DashboardBuilderServicesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderServicesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DashboardBuilderServicesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderServicesModule, imports: [CommonModule] });
DashboardBuilderServicesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderServicesModule, providers: [DashboardStructureService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderServicesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    providers: [DashboardStructureService],
                }]
        }] });

class DashboardManagementService {
    constructor(structureService, bindingMiddleware) {
        this.structureService = structureService;
        this.bindingMiddleware = 'dashboard-data-converter';
        if (bindingMiddleware) {
            this.bindingMiddleware = bindingMiddleware;
        }
    }
    createTopLevelElement(element) {
        const elementNode = {
            type: 'element',
            element,
            children: [],
        };
        return {
            type: 'grid',
            element: {
                tag: 'dashboard-grid-tile',
                properties: {
                    header: dashboardElements.getDescription(element.tag).documentation.name,
                    lock: false,
                },
            },
            children: [elementNode],
        };
    }
    addTopLevelElement(element) {
        const gridNode = this.createTopLevelElement(element);
        this.structureService.addToRoot(gridNode);
        return gridNode;
    }
    addBinding(node, bindingTemplate, property) {
        const binding = JSON.parse(JSON.stringify(bindingTemplate));
        let converter = node.children.find((child) => child.element.tag === this.bindingMiddleware && child.element.properties.property === property.name);
        if (!converter) {
            const propertyIsArray = property.index !== undefined;
            const converterElement = {
                tag: this.bindingMiddleware,
                properties: {
                    property: property.name,
                    propertyIsArray,
                },
            };
            converter = this.structureService.addElementTo(node, converterElement);
        }
        binding.properties.property = `value${property.index ?? 0}`;
        binding.properties.propertyType = getElementPropertyType(node.element.tag, property);
        return this.structureService.addElementTo(converter, binding);
    }
    removeBindings(node) {
        node.children
            .filter((child) => child.element.tag === this.bindingMiddleware)
            .forEach((converter) => this.structureService.removeNode(converter));
    }
    getBindings(node) {
        return node.children
            .filter((child) => child.element.tag === this.bindingMiddleware)
            .map((converter) => converter.children.map((binding) => {
            const propertyName = binding.element.properties.property;
            const index = Number(propertyName.substr('value'.length));
            return {
                property: {
                    name: converter.element.properties.property,
                    ...(converter.element.properties.propertyIsArray ? { index } : {}),
                },
                instance: binding.element,
            };
        }))
            .reduce((acc, cur) => acc.concat(cur), []);
    }
    setBindings(node, bindings) {
        this.removeBindings(node);
        bindings.forEach((binding) => this.addBinding(node, binding.instance, binding.property));
    }
}
DashboardManagementService.BINDING_MIDDLEWARE_TOKEN = 'BindingMiddleWareToken';
DashboardManagementService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardManagementService, deps: [{ token: DashboardStructureService }, { token: DashboardManagementService.BINDING_MIDDLEWARE_TOKEN, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
DashboardManagementService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardManagementService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardManagementService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: DashboardStructureService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DashboardManagementService.BINDING_MIDDLEWARE_TOKEN]
                }] }]; } });

const TILE_LINK_SERVICE = new InjectionToken('TileLinkService');
class TileLinkService {
    constructor() {
        this.availableLinks$ = undefined;
        this.loading$ = of(false);
    }
    searchTermChanged() {
        // pass
    }
    getLinkData$() {
        return of(undefined);
    }
}
const defaultTileLinkServiceProvider = { provide: TILE_LINK_SERVICE, useClass: TileLinkService };

class TrackingService {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    track(_module, _event, _data = {}, _single = undefined) {
        // noop
    }
    flush() {
        // noop
    }
}
const defaultTrackingServiceProvider = { provide: TrackingService, useClass: TrackingService };

/**
 * Generated bundle index. Do not edit.
 */

export { DashboardBuilderServicesModule, DashboardManagementService, DashboardStructureService, TILE_LINK_SERVICE, TileLinkService, TrackingService, defaultTileLinkServiceProvider, defaultTrackingServiceProvider };
